import { useMemo } from 'react'
import { useApp } from '@/context/AppContext'

/**
 * Hook that returns break values filtered by province when applicable.
 * When the break is "regiondetailed" and a province is selected,
 * only returns sub-regions within that province.
 */
export function useFilteredBreakValues() {
  const { state } = useApp()
  const { data, filters } = state
  const { selectedBreak, selectedProvince } = filters

  const breakDef = data?.breaks.find(b => b.id === selectedBreak)

  const filteredBreakValues = useMemo(() => {
    if (!breakDef) return []

    let values = breakDef.values

    // Exclude "Not sure/ I don't recall" from party break
    if (selectedBreak === 'party') {
      values = values.filter(v => !v.toLowerCase().includes('not sure'))
    }

    // Filter detailed regions by selected province or provincezone value
    if (selectedBreak === 'regiondetailed' && selectedProvince) {
      // Check if selectedProvince is actually a provincezone value (e.g., "Ontario Rural")
      const isProvincezoneValue = selectedProvince.endsWith(' Rural') || selectedProvince.endsWith(' Urban')

      if (isProvincezoneValue) {
        // For provincezone values, we filter the regiondetailed by the province prefix
        // and the data lookup will use the provincezone filter key
        const provinceFromZone = selectedProvince.replace(/ (Rural|Urban)$/, '')
        const provinceMap: Record<string, string> = {
          'British Columbia': 'British Columbia',
          'Alberta': 'Alberta',
          'Saskatchewan': 'Saskatchewan',
          'Manitoba': 'Manitoba',
          'Ontario': 'Ontario',
          'Quebec': 'Quebec',
          'New Brunswick': 'New Brunswick',
          'Nova Scotia': 'Nova Scotia',
          'Prince Edward Island': 'Prince Edward Island',
          'Newfoundland & Labrador': 'Newfoundland & Labrador',
          'North Provinces': 'Northern Territories',
        }
        const prefix = provinceMap[provinceFromZone]
        if (prefix) {
          return values.filter(v => v.startsWith(prefix))
        }
      } else {
        // Map province names to their prefix in RegionAngus values
        const provinceMap: Record<string, string> = {
          'British Columbia': 'British Columbia',
          'Alberta': 'Alberta',
          'Saskatchewan': 'Saskatchewan',
          'Manitoba': 'Manitoba',
          'Ontario': 'Ontario',
          'Quebec': 'Quebec',
          'New Brunswick': 'New Brunswick',
          'Nova Scotia': 'Nova Scotia',
          'Prince Edward Island': 'Prince Edward Island',
          'Newfoundland & Labrador': 'Newfoundland & Labrador',
          'Yukon': 'Northern Territories',
          'NWT': 'Northern Territories',
          'Nunavut': 'Northern Territories',
        }

        const prefix = provinceMap[selectedProvince]
        if (prefix) {
          return values.filter(v => v.startsWith(prefix))
        }
      }
    }

    return values
  }, [breakDef, selectedBreak, selectedProvince])

  return {
    breakValues: filteredBreakValues,
    isFiltered: selectedBreak === 'regiondetailed' && !!selectedProvince,
    totalValues: breakDef?.values.length || 0,
  }
}
